import type {
  ActionType,
  ProColumns,
  FormInstance,
} from "@ant-design/pro-components";
import { useRef } from "react";

import type { AxiosResponse } from "axios";

interface FetchResult<T> {
  data: T[];
  success: boolean;
  total: number;
}

export type ApiFunction = (params: any) => Promise<AxiosResponse>;
type FormatterFunction<T> = (params: T) => any;
type ExtractorFunction<TData> = (resData: any) => {
  data: TData[];
  total: number;
};

/**
 * 创建一个通用的列表获取函数
 * @param apiFunction - 外部的 API 调用函数
 * @param formatter - 参数格式化函数 可选
 * @returns 返回一个新的、可复用的 fetch 函数
 */
export const createProTableFetcher = <TParams, TData>(
  apiFunction: ApiFunction,
  formatter?: FormatterFunction<TParams>,
  extractor?: ExtractorFunction<TData>,
): ((params: TParams) => Promise<FetchResult<TData>>) => {
  return async (params: TParams): Promise<FetchResult<TData>> => {
    try {
      const formattedParams = formatter ? formatter(params) : params;
      const res = await apiFunction(formattedParams || {});

      const { data, total } = extractor
        ? extractor(res.data)
        : { data: res.data.data.list ?? [], total: res.data.data.total ?? 0 };

      return {
        data,
        success: true,
        total,
      };
    } catch (error) {
      console.error(error);
      return { data: [], success: false, total: 0 };
    }
  };
};

type UseTableFn<T, P> = {
  fetchFn: ApiFunction;
  formatter?: FormatterFunction<T>;
  extractor?: ExtractorFunction<P>;
  columns: ProColumns<P>[];
};

export function useTable<T, P>({
  fetchFn,
  formatter,
  columns,
  extractor,
}: UseTableFn<T, P>) {
  const actionRef = useRef<ActionType>();
  const formRef = useRef<FormInstance>();
  const fetchDatasource = createProTableFetcher<T, P>(
    fetchFn,
    formatter,
    extractor,
  );

  const _columns = columns.map((c) => {
    const defaultSorter = (a: any, b: any) => {
      const valA = a[c.dataIndex as keyof P];
      const valB = b[c.dataIndex as keyof P];
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return 0;
    };

    const hasSorterSetting = c.sorter !== void 0;

    return {
      ...c,
      sorter: hasSorterSetting ? c.sorter : defaultSorter,
    };
  });

  return {
    actionRef,
    formRef,
    fetchDatasource,
    columns: _columns,
  };
}

export type Search = {
  current?: number;
  pageSize?: number;
};
